import React, { useState, useContext } from 'react'
import { Trans, t } from '@lingui/macro'

import { useFeatureFlags } from 'front-end/src/app/hooks/useFeatureFlags'
import {
  Tooltip,
  Box,
  FlexBoxGrid,
  ButtonClean
} from 'front-end/src/components'

import { TopicElementTypeEnum } from '../../../../../../types/TopicElementTypeEnum'
import { apolloClientPrivate } from '../../../../graphql/apolloClient'
import { useTopicForEditQuery } from '../../../topic-edit-page/TopicEditPage'
import Loader from '../../../../components/common/loader/Loader'

import {
  CreateElementMutationFn,
  CreateElementDocument
} from './operations/createElement.codegen'
import { useEmbedDomainsQuery } from '../../../../app/hooks/useEmbedDomains.codegen'
import { TopicEditingContext } from '../../../topic-edit-page/TopicEditContext'
import { css } from '@emotion/react'
import IconLogin from 'front-end/src/components/common/icon/IconLogin'
import ElementVideo from 'public/assets/img/element-video.svg'
import ElementIframe from 'public/assets/img/element-iframe.svg'
import ElementOneDriveFile from 'public/assets/img/element-one-drive-file.svg'
import ElementGoogleDriveFile from 'public/assets/img/element-google-drive-file.svg'
import ElementFile from 'public/assets/img/element-file.svg'
import ElementLink from 'public/assets/img/element-link.svg'
import ElementQuiz from 'public/assets/img/element-quiz.svg'
import ElementText from 'public/assets/img/element-text.svg'

export const ChooseElement = ({
  isModalMode,
  onChosen
}: {
  isModalMode?: boolean
  onChosen?: () => any
}) => {
  const [creating, setCreating] = useState(false)
  const { refetch: refetchTopic } = useTopicForEditQuery()

  const { setCurrentElementIndex, topic, setPowerPointImportModalVisible } =
    useContext(TopicEditingContext)
  const { featureFlags } = useFeatureFlags()
  const { data: embedDomainsData } = useEmbedDomainsQuery()

  const createElement = (pageType) => async () => {
    setCreating(true)
    try {
      await apolloClientPrivate.mutate<CreateElementMutationFn>({
        mutation: CreateElementDocument,
        variables: {
          topicId: topic.id,
          type: pageType
        }
      })

      const { data } = await refetchTopic()

      if (data && data.topic?.__typename === 'TopicModelAsEditor') {
        const elements = data.topic?.draftVersion?.elements
        if (elements) {
          setCurrentElementIndex(elements.length - 1)
        }
      }

      onChosen && onChosen()
    } catch (err) {
      console.error(err)
    } finally {
      setCreating(false)
    }
  }

  const countOfBasicTiles = 5
  const countOfAllTiles =
    countOfBasicTiles +
    +(featureFlags?.gsuite || 0) +
    +(featureFlags?.microsoft_365 || 0) +
    +(embedDomainsData?.organisation.embed_domains?.enabled || 0)
  const divisor =
    countOfAllTiles > 6
      ? countOfAllTiles / 1.7 // wrap in two lines
      : countOfAllTiles

  return (
    <>
      {creating && (
        <div className="choose-page">
          <h3>
            <Trans>Please wait</Trans>
          </h3>
          <Loader />
          <p>
            <Trans>Creating a new element...</Trans>
          </p>
        </div>
      )}

      {!creating && (
        <Box className="choose-page">
          {!isModalMode && (
            <h3 className="choose-page__add-element">
              <Trans>Add another element</Trans>
            </h3>
          )}

          <FlexBoxGrid
            className="page-options"
            css={css`
              .wrapper {
                padding: 0;
              }
              .page-option {
                flex: 0;
              }
              .page-option a {
                margin: 0;
              }
              .Tooltip {
                flex: 1;
                max-width: 100%;
              }
            `}
            columns={
              isModalMode
                ? [
                    '100px',
                    `${540 / divisor}px`,
                    `${540 / divisor}px`,
                    `${540 / divisor}px`
                  ]
                : [
                    '100px',
                    `${(700 - 8) / divisor}px`,
                    `${(700 - 8) / divisor}px`,
                    `${(700 - 8) / divisor}px`
                  ]
            }
            marginOutside
            columnGap="8px"
            rowGap="8px"
            padding="16px 0"
          >
            <Box className="page-option">
              <Tooltip
                text={
                  <Trans>
                    Use text and images to tell your story. You can copy and
                    paste from the web or PDF, or write directly on the page.
                  </Trans>
                }
              >
                <a
                  onClick={createElement(TopicElementTypeEnum.TEXT)}
                  className="page-option__text clickable"
                >
                  <img
                    className="element-icon"
                    src={ElementText}
                    alt={t`Text and images`}
                  />
                  <div className="label">
                    <Trans>Text and images</Trans>
                  </div>
                </a>
              </Tooltip>
            </Box>
            <Box className="page-option">
              <Tooltip
                text={
                  <Trans>
                    Upload your own video/audio content or link to existing
                    videos on YouTube. Looop will compress and format your
                    videos for mobile devices.
                  </Trans>
                }
              >
                <a
                  onClick={createElement(TopicElementTypeEnum.MEDIA)}
                  className="clickable"
                >
                  <img
                    className="element-icon"
                    src={ElementVideo}
                    alt={t`Video`}
                  />
                  <div className="label">
                    <Trans>Media</Trans>
                  </div>
                </a>
              </Tooltip>
            </Box>
            <Box className="page-option">
              <Tooltip
                text={
                  <Trans>
                    Check knowledge with a multiple choice quiz. Give people
                    feedback to let them know why they were right or wrong.
                  </Trans>
                }
              >
                <a
                  onClick={createElement(TopicElementTypeEnum.QUIZ_SINGLE)}
                  className="page-option__quiz clickable"
                >
                  <img
                    className="element-icon"
                    src={ElementQuiz}
                    alt={t`Quiz`}
                  />
                  <div className="label">
                    <Trans>Quiz</Trans>
                  </div>
                </a>
              </Tooltip>
            </Box>
            <Box className="page-option">
              <Tooltip
                text={
                  <Trans>
                    Add a link to a web page. This content will be loaded
                    without leaving the current page.
                  </Trans>
                }
              >
                <a
                  onClick={createElement(TopicElementTypeEnum.LINK)}
                  className="page-option__link clickable"
                >
                  <img className="element-icon" src={ElementLink} />
                  <div className="label">
                    <Trans>Link</Trans>
                  </div>
                </a>
              </Tooltip>
            </Box>
            <Box className="page-option">
              <Tooltip
                text={
                  <Trans>
                    Upload a file, add a description, and make it available for
                    your users to download.
                  </Trans>
                }
              >
                <a
                  onClick={createElement(TopicElementTypeEnum.DOCUMENT)}
                  className="page-option__file clickable"
                >
                  <img className="element-icon" src={ElementFile} />
                  <div className="label">
                    <Trans>File</Trans>
                  </div>
                </a>
              </Tooltip>
            </Box>
            {featureFlags?.gsuite && (
              <Box className="page-option">
                <Tooltip text={<Trans>Add link to Google file</Trans>}>
                  <a
                    onClick={createElement(
                      TopicElementTypeEnum.GOOGLE_DRIVE_FILE
                    )}
                    className="page-option__gdrive clickable"
                  >
                    <img
                      className="element-icon"
                      src={ElementGoogleDriveFile}
                    />
                    <div className="label">
                      <Trans>Google file</Trans>
                    </div>
                  </a>
                </Tooltip>
              </Box>
            )}
            {featureFlags?.microsoft_365 && (
              <Box className="page-option">
                <Tooltip text={<Trans>Add link to Microsoft file</Trans>}>
                  <a
                    onClick={createElement(TopicElementTypeEnum.MICROSOFT_FILE)}
                    className="page-option__gdrive clickable"
                  >
                    <img className="element-icon" src={ElementOneDriveFile} />
                    <div className="label">
                      <Trans>Microsoft file</Trans>
                    </div>
                  </a>
                </Tooltip>
              </Box>
            )}
            {embedDomainsData?.organisation.embed_domains?.enabled && (
              <Box className="page-option">
                <Tooltip
                  text={
                    <Trans>
                      Add a link or embed code of a web page. This content will
                      be loaded without leaving the current page.
                    </Trans>
                  }
                >
                  <a
                    onClick={createElement(TopicElementTypeEnum.EMBED_LINK)}
                    className="page-option__file clickable"
                  >
                    <img className="element-icon" src={ElementIframe} />
                    <div className="label">
                      <Trans>Embed code</Trans>
                    </div>
                  </a>
                </Tooltip>
              </Box>
            )}
          </FlexBoxGrid>
          <div className="import-options">
            <ButtonClean
              onClick={() => {
                setPowerPointImportModalVisible(true)
                if (isModalMode) {
                  onChosen && onChosen()
                }
              }}
              leftIcon={<IconLogin />}
            >
              <Trans>Import from file</Trans>
            </ButtonClean>
          </div>
        </Box>
      )}
    </>
  )
}
